import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { DownloadButton } from '@/utils/DownloadButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  UserCheck, 
  Coffee, 
  UserX,
  RefreshCw,
  Clock,
  AlertTriangle,
  Search,
  MapPin,
  Timer,
  LogIn,
  LogOut,
  RotateCcw,
  XCircle,
  AlertCircle,
  ChevronRight,
  CalendarDays,
  CalendarCheck,
  CalendarX,
  CheckCircle,
  Wrench,
  History,
  FileText,
  Download,
  FileSpreadsheet,
  User,
  Eye,
  Edit,
  MessageSquare,
  Filter,
  Plus,
  Trash2,
  Hourglass,
  Check,
  X,
  ListChecks,
  Square,
  CheckSquare
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const API = API_URL;

// Resolution types configuration
const RESOLUTION_TYPES = [
  { value: 'public_holiday', label: { fr: 'Jour férié', en: 'Public holiday' } },
  { value: 'collective_absence', label: { fr: 'Absence collective', en: 'Collective absence' } },
  { value: 'absence_confirmed', label: { fr: 'Absence confirmée', en: 'Confirmed absence' } },
  { value: 'sickness', label: { fr: 'Maladie', en: 'Sickness' } },
  { value: 'approved_leave', label: { fr: 'Congé approuvé', en: 'Approved leave' } },
  { value: 'justified_late', label: { fr: 'Retard justifié', en: 'Justified late' } },
  { value: 'forgot_punch', label: { fr: 'Oubli de punch', en: 'Forgot to punch' } },
  { value: 'ignored', label: { fr: 'Ignorée', en: 'Ignored' } },
  { value: 'other', label: { fr: 'Autre', en: 'Other' } }
];

export default function DashboardPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId, loading: tenantLoading, error: tenantError } = useTenant();
  
  // Check if user is a supervisor
  const isSupervisor = user?.role === 'tenant_supervisor';
  const isAdmin = ['platform_superadmin', 'tenant_superuser', 'tenant_admin'].includes(user?.role);
  const supervisorShiftId = user?.shiftId;
  
  const [presence, setPresence] = useState({ 
    stats: { total: 0, present: 0, onBreak: 0, out: 0, anomaliesCount: 0 }, 
    employees: [],
    anomalies: { lateArrivals: [], longBreaks: [], absences: [], incompletePunches: [] }
  });
  const [recentPunches, setRecentPunches] = useState([]);
  const [liveWorking, setLiveWorking] = useState({ totalWorking: 0, totalOvertime: 0, employees: [] });
  const [scheduleStatus, setScheduleStatus] = useState({ stats: {}, scheduled: [], notScheduled: [] });
  const [treatedAlerts, setTreatedAlerts] = useState({ lookup: {} });
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Alerts dialog state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlertType, setSelectedAlertType] = useState(null);
  
  // Treatment dialog state
  const [treatDialogOpen, setTreatDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [treatmentForm, setTreatmentForm] = useState({
    resolutionType: '',
    comment: ''
  });
  const [treating, setTreating] = useState(false);
  
  // History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [treatmentHistory, setTreatmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyEmployees, setHistoryEmployees] = useState([]);
  const [historyFilters, setHistoryFilters] = useState({
    employeeId: 'all',
    alertType: 'all',
    startDate: '',
    endDate: ''
  });
  const [historyStats, setHistoryStats] = useState(null);
  
  // Employee profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedEmployeeProfile, setSelectedEmployeeProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  // Punch correction dialog state
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionForm, setCorrectionForm] = useState({
    correctionType: 'add_missing',
    eventType: 'IN',
    correctedTime: '',
    reason: 'forgot_punch',
    comment: ''
  });
  const [employeePunches, setEmployeePunches] = useState([]);
  const [loadingPunches, setLoadingPunches] = useState(false);
  const [submittingCorrection, setSubmittingCorrection] = useState(false);
  
  // Comment dialog state
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentForm, setCommentForm] = useState({
    commentType: 'general',
    content: '',
    isPrivate: false
  });
  const [employeeComments, setEmployeeComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Overtime validation dialog state
  const [overtimeDialogOpen, setOvertimeDialogOpen] = useState(false);
  const [pendingOvertime, setPendingOvertime] = useState([]);
  const [loadingOvertime, setLoadingOvertime] = useState(false);
  const [overtimeApprovalForm, setOvertimeApprovalForm] = useState({
    action: 'approve',
    adjustedMinutes: null,
    comment: ''
  });
  const [selectedOvertimeRecord, setSelectedOvertimeRecord] = useState(null);
  const [submittingOvertimeReview, setSubmittingOvertimeReview] = useState(false);
  
  // Filters
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedShift, setSelectedShift] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllEmployees, setShowAllEmployees] = useState(false);

  // Bulk selection state for alerts
  const [bulkSelected, setBulkSelected] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ resolutionType: '', comment: '' });
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Initialize shift filter for supervisors
  useEffect(() => {
    if (isSupervisor && supervisorShiftId && !showAllEmployees) {
      setSelectedShift(supervisorShiftId);
    }
  }, [isSupervisor, supervisorShiftId, showAllEmployees]);

  const fetchSitesAndZones = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const [sitesRes, zonesRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/sites?tenantId=${effectiveTenantId}`),
        axios.get(`${API}/zones?tenantId=${effectiveTenantId}`),
        axios.get(`${API}/shifts?tenantId=${effectiveTenantId}`)
      ]);
      setSites(sitesRes.data);
      setZones(zonesRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      console.error('Failed to fetch sites/zones/shifts:', error);
    }
  }, [effectiveTenantId]);

  const fetchTreatedAlerts = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      const token = localStorage.getItem('punchtm-token');
      const response = await axios.get(`${API}/alerts/treated-today?tenantId=${effectiveTenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTreatedAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch treated alerts:', error);
    }
  }, [effectiveTenantId]);

  const fetchPresence = useCallback(async () => {
    if (!effectiveTenantId) return;
    
    try {
      let url = `${API}/presence?tenantId=${effectiveTenantId}`;
      if (selectedSite !== 'all') url += `&siteId=${selectedSite}`;
      if (selectedZone !== 'all') url += `&zoneId=${selectedZone}`;
      if (selectedShift !== 'all') url += `&shiftId=${selectedShift}`;
      if (selectedStatus !== 'all') url += `&status=${selectedStatus}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      let punchesUrl = `${API}/presence/recent-punches?tenantId=${effectiveTenantId}&limit=10`;
      if (selectedShift !== 'all') punchesUrl += `&shiftId=${selectedShift}`;
      
      let liveWorkingUrl = `${API}/presence/live-working?tenantId=${effectiveTenantId}`;
      if (selectedShift !== 'all') liveWorkingUrl += `&shiftId=${selectedShift}`;
      
      let scheduleUrl = `${API}/schedules/today?tenantId=${effectiveTenantId}`;
      if (selectedShift !== 'all') scheduleUrl += `&shiftId=${selectedShift}`;
      
      const [presenceRes, punchesRes, liveWorkingRes, scheduleRes] = await Promise.all([
        axios.get(url),
        axios.get(punchesUrl),
        axios.get(liveWorkingUrl),
        axios.get(scheduleUrl)
      ]);
      
      setPresence(presenceRes.data);
      setRecentPunches(punchesRes.data);
      setLiveWorking(liveWorkingRes.data);
      setScheduleStatus(scheduleRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch presence:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [effectiveTenantId, selectedSite, selectedZone, selectedShift, selectedStatus, searchQuery, t]);

  useEffect(() => {
    fetchSitesAndZones();
  }, [fetchSitesAndZones]);

  useEffect(() => {
    fetchPresence();
    fetchTreatedAlerts();
    
    // Auto-refresh every 10 seconds for industrial use
    const interval = setInterval(() => {
      fetchPresence();
      fetchTreatedAlerts();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchPresence, fetchTreatedAlerts]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPresence();
    fetchTreatedAlerts();
  };

  const getStatusBadge = (status) => {
    const config = {
      IN: { 
        label: language === 'fr' ? 'Présent' : 'Present', 
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200' 
      },
      BREAK: { 
        label: language === 'fr' ? 'Pause' : 'Break', 
        className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200' 
      },
      OUT: { 
        label: language === 'fr' ? 'Sorti' : 'Out', 
        className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200' 
      }
    };
    
    const { label, className } = config[status] || config.OUT;
    return <Badge className={`${className} border font-medium`}>{label}</Badge>;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getPunchTypeLabel = (type) => {
    const labels = {
      IN: language === 'fr' ? 'Entrée' : 'Clock In',
      BREAK_START: language === 'fr' ? 'Début pause' : 'Break Start',
      BREAK_END: language === 'fr' ? 'Fin pause' : 'Break End',
      OUT: language === 'fr' ? 'Sortie' : 'Clock Out'
    };
    return labels[type] || type;
  };

  const getPunchIcon = (type) => {
    const icons = {
      IN: <LogIn size={14} className="text-green-600" />,
      BREAK_START: <Coffee size={14} className="text-orange-600" />,
      BREAK_END: <RotateCcw size={14} className="text-blue-600" />,
      OUT: <LogOut size={14} className="text-red-600" />
    };
    return icons[type] || null;
  };

  // Check if an alert is already treated
  const isAlertTreated = (alertType, employeeId) => {
    const key = `${alertType}_${employeeId}`;
    return !!treatedAlerts.lookup?.[key];
  };

  // Filter out treated alerts from the display
  const filterUntreatedAlerts = (alerts, alertType) => {
    if (!alerts) return [];
    return alerts.filter(alert => !isAlertTreated(alertType, alert.employeeId));
  };

  // Get untreated counts
  const getUntreatedCount = (alertType, alerts) => {
    return filterUntreatedAlerts(alerts, alertType).length;
  };

  const statCards = [
    { label: language === 'fr' ? 'Attendus' : 'Expected', value: presence.stats.expected || 0, icon: Users, color: 'text-slate-600 bg-slate-100', borderColor: 'border-slate-200' },
    { label: t('present'), value: presence.stats.present, icon: UserCheck, color: 'text-green-600 bg-green-100', borderColor: 'border-green-200' },
    { label: language === 'fr' ? 'Retards' : 'Late', value: presence.stats.late || 0, icon: AlertTriangle, color: 'text-amber-600 bg-amber-100', borderColor: 'border-amber-200' },
    { label: language === 'fr' ? 'Absents' : 'Absent', value: presence.stats.absent || 0, icon: UserX, color: 'text-red-600 bg-red-100', borderColor: 'border-red-200' },
    { label: t('onBreak'), value: presence.stats.onBreak, icon: Coffee, color: 'text-orange-600 bg-orange-100', borderColor: 'border-orange-200' }
  ];

  // Alert types configuration with untreated counts
  const alertTypes = [
    {
      id: 'lateArrivals',
      label: language === 'fr' ? 'Retards' : 'Late Arrivals',
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      count: getUntreatedCount('lateArrivals', presence.anomalies?.lateArrivals),
      totalCount: presence.anomalies?.lateArrivals?.length || 0,
      data: filterUntreatedAlerts(presence.anomalies?.lateArrivals, 'lateArrivals')
    },
    {
      id: 'absences',
      label: language === 'fr' ? 'Absences' : 'Absences',
      icon: UserX,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      count: getUntreatedCount('absences', presence.anomalies?.absences),
      totalCount: presence.anomalies?.absences?.length || 0,
      data: filterUntreatedAlerts(presence.anomalies?.absences, 'absences')
    },
    {
      id: 'longBreaks',
      label: language === 'fr' ? 'Pauses longues' : 'Long Breaks',
      icon: Coffee,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      count: getUntreatedCount('longBreaks', presence.anomalies?.longBreaks),
      totalCount: presence.anomalies?.longBreaks?.length || 0,
      data: filterUntreatedAlerts(presence.anomalies?.longBreaks, 'longBreaks')
    },
    {
      id: 'incompletePunches',
      label: language === 'fr' ? 'Punch incomplets' : 'Incomplete Punches',
      icon: AlertCircle,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      count: getUntreatedCount('incompletePunches', presence.anomalies?.incompletePunches),
      totalCount: presence.anomalies?.incompletePunches?.length || 0,
      data: filterUntreatedAlerts(presence.anomalies?.incompletePunches, 'incompletePunches')
    }
  ];

  const totalUntreatedAnomalies = alertTypes.reduce((sum, a) => sum + a.count, 0);

  const openAlertDialog = (alertType) => {
    setSelectedAlertType(alertType);
    setBulkSelected([]);
    setAlertDialogOpen(true);
  };

  const openTreatDialog = (employee, alertType) => {
    setSelectedEmployee({ ...employee, alertType });
    setTreatmentForm({ resolutionType: '', comment: '' });
    setTreatDialogOpen(true);
  };

  const handleTreatAlert = async () => {
    if (!selectedEmployee || !treatmentForm.resolutionType) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner un type de résolution' : 'Please select a resolution type');
      return;
    }

    setTreating(true);
    try {
      const token = localStorage.getItem('punchtm-token');
      const today = new Date().toISOString().split('T')[0];
      
      await axios.post(`${API}/alerts/treat`, {
        tenantId: effectiveTenantId,
        alertType: selectedEmployee.alertType,
        employeeId: selectedEmployee.employeeId,
        employeeCode: selectedEmployee.employeeCode,
        employeeName: selectedEmployee.employeeName,
        date: today,
        resolutionType: treatmentForm.resolutionType,
        comment: treatmentForm.comment || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'fr' ? 'Alerte traitée avec succès' : 'Alert treated successfully');
      setTreatDialogOpen(false);
      setAlertDialogOpen(false);
      fetchTreatedAlerts();
    } catch (error) {
      console.error('Failed to treat alert:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du traitement' : 'Failed to treat alert'));
    } finally {
      setTreating(false);
    }
  };

  // Bulk alert treatment
  const toggleBulkSelect = (emp) => {
    setBulkSelected(prev => {
      const exists = prev.find(e => e.employeeId === emp.employeeId);
      if (exists) return prev.filter(e => e.employeeId !== emp.employeeId);
      return [...prev, emp];
    });
  };

  const toggleSelectAll = (alertData) => {
    if (bulkSelected.length === alertData.length) {
      setBulkSelected([]);
    } else {
      setBulkSelected([...alertData]);
    }
  };

  const openBulkDialog = () => {
    setBulkForm({ resolutionType: '', comment: '' });
    setBulkDialogOpen(true);
  };

  const handleBulkTreat = async () => {
    if (!bulkForm.resolutionType || bulkSelected.length === 0) {
      toast.error(language === 'fr' ? 'Sélectionnez un motif' : 'Select a resolution type');
      return;
    }
    setBulkProcessing(true);
    try {
      const token = localStorage.getItem('punchtm-token');
      const res = await axios.post(`${API}/alerts/bulk-treat`, {
        tenantId: effectiveTenantId,
        alertType: selectedAlertType,
        employees: bulkSelected.map(e => ({
          employeeId: e.employeeId,
          employeeCode: e.employeeCode,
          employeeName: e.employeeName,
        })),
        resolutionType: bulkForm.resolutionType,
        comment: bulkForm.comment || null,
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`${res.data.treatedCount} ${language === 'fr' ? 'alertes traitées' : 'alerts treated'}`);
      setBulkDialogOpen(false);
      setAlertDialogOpen(false);
      setBulkSelected([]);
      fetchTreatedAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setBulkProcessing(false);
    }
  };

  const fetchHistory = async (filters = historyFilters) => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('punchtm-token');
      
      // Build query params
      let url = `${API}/alerts/history?tenantId=${effectiveTenantId}&limit=100`;
      if (filters.employeeId && filters.employeeId !== 'all') {
        url += `&employeeId=${filters.employeeId}`;
      }
      if (filters.alertType && filters.alertType !== 'all') {
        url += `&alertType=${filters.alertType}`;
      }
      if (filters.startDate) {
        url += `&startDate=${filters.startDate}`;
      }
      if (filters.endDate) {
        url += `&endDate=${filters.endDate}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTreatmentHistory(response.data.treatments || []);
      setHistoryStats(response.data.stats || null);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement de l\'historique' : 'Failed to load history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchHistoryEmployees = async () => {
    try {
      const token = localStorage.getItem('punchtm-token');
      const response = await axios.get(`${API}/alerts/employees-list?tenantId=${effectiveTenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryEmployees(response.data.allEmployees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const openHistoryDialog = () => {
    setHistoryDialogOpen(true);
    setHistoryFilters({ employeeId: 'all', alertType: 'all', startDate: '', endDate: '' });
    fetchHistory({ employeeId: 'all', alertType: 'all', startDate: '', endDate: '' });
    fetchHistoryEmployees();
  };

  const handleHistoryFilterChange = (key, value) => {
    const newFilters = { ...historyFilters, [key]: value };
    setHistoryFilters(newFilters);
    fetchHistory(newFilters);
  };

  const buildAlertExportUrl = (format) => {
    let url = `${API}/alerts/export?tenantId=${effectiveTenantId}&format=${format}`;
    if (historyFilters.employeeId && historyFilters.employeeId !== 'all') {
      url += `&employeeId=${historyFilters.employeeId}`;
    }
    if (historyFilters.alertType && historyFilters.alertType !== 'all') {
      url += `&alertType=${historyFilters.alertType}`;
    }
    if (historyFilters.startDate) url += `&startDate=${historyFilters.startDate}`;
    if (historyFilters.endDate) url += `&endDate=${historyFilters.endDate}`;
    return url;
  };

  // Overtime functions
  const fetchPendingOvertime = async () => {
    if (!effectiveTenantId) return;
    
    setLoadingOvertime(true);
    try {
      const token = localStorage.getItem('punchtm-token');
      const response = await axios.get(
        `${API}/overtime/pending?tenantId=${effectiveTenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingOvertime(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch pending overtime:', error);
    } finally {
      setLoadingOvertime(false);
    }
  };

  const openOvertimeDialog = () => {
    setOvertimeDialogOpen(true);
    fetchPendingOvertime();
  };

  const handleGenerateDailyOvertime = async () => {
    try {
      const token = localStorage.getItem('punchtm-token');
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const dateStr = yesterday.toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API}/overtime/generate-daily?tenantId=${effectiveTenantId}&date=${dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.created > 0) {
        toast.success(
          language === 'fr' 
            ? `${response.data.created} demande(s) d'heures supplémentaires générée(s)`
            : `${response.data.created} overtime request(s) generated`
        );
        fetchPendingOvertime();
      } else {
        toast.info(
          language === 'fr'
            ? 'Aucune heure supplémentaire détectée'
            : 'No overtime detected'
        );
      }
    } catch (error) {
      console.error('Failed to generate overtime:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la génération' : 'Generation failed');
    }
  };

  const selectOvertimeForReview = (record) => {
    setSelectedOvertimeRecord(record);
    setOvertimeApprovalForm({
      action: 'approve',
      adjustedMinutes: record.overtimeMinutes,
      comment: ''
    });
  };

  const handleSubmitOvertimeReview = async () => {
    if (!selectedOvertimeRecord) return;
    
    setSubmittingOvertimeReview(true);
    try {
      const token = localStorage.getItem('punchtm-token');
      
      await axios.post(
        `${API}/overtime/${selectedOvertimeRecord.id}/review`,
        {
          action: overtimeApprovalForm.action,
          adjustedMinutes: overtimeApprovalForm.action === 'approve' 
            ? parseInt(overtimeApprovalForm.adjustedMinutes) || 0 
            : null,
          comment: overtimeApprovalForm.comment || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        language === 'fr'
          ? overtimeApprovalForm.action === 'approve' 
            ? 'Heures supplémentaires approuvées'
            : 'Heures supplémentaires refusées'
          : overtimeApprovalForm.action === 'approve'
            ? 'Overtime approved'
            : 'Overtime rejected'
      );
      
      setSelectedOvertimeRecord(null);
      fetchPendingOvertime();
    } catch (error) {
      console.error('Failed to review overtime:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la validation' : 'Review failed');
    } finally {
      setSubmittingOvertimeReview(false);
    }
  };

  const formatMinutesToDisplay = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h${m.toString().padStart(2, '0')}`;
  };

  const openEmployeeProfile = async (employee) => {
    setProfileDialogOpen(true);
    setLoadingProfile(true);
    setSelectedEmployeeProfile(null);
    
    try {
      const token = localStorage.getItem('punchtm-token');
      const response = await axios.get(
        `${API}/employee/${employee.employeeId}/quick-profile?tenantId=${effectiveTenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedEmployeeProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch employee profile:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement du profil' : 'Failed to load profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  // Punch Correction functions
  const openCorrectionDialog = async () => {
    if (!selectedEmployeeProfile) return;
    
    setCorrectionDialogOpen(true);
    setCorrectionForm({
      correctionType: 'add_missing',
      eventType: 'IN',
      correctedTime: '',
      reason: 'forgot_punch',
      comment: ''
    });
    setLoadingPunches(true);
    
    try {
      const token = localStorage.getItem('punchtm-token');
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(
        `${API}/employee/${selectedEmployeeProfile.employee.id}/punches?date=${today}&tenantId=${effectiveTenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmployeePunches(response.data.punches || []);
    } catch (error) {
      console.error('Failed to fetch punches:', error);
    } finally {
      setLoadingPunches(false);
    }
  };

  const handleSubmitCorrection = async () => {
    if (!selectedEmployeeProfile || !correctionForm.correctedTime) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return;
    }

    setSubmittingCorrection(true);
    try {
      const token = localStorage.getItem('punchtm-token');
      const today = new Date().toISOString().split('T')[0];
      
      await axios.post(`${API}/punch/correction`, {
        employeeId: selectedEmployeeProfile.employee.id,
        correctionType: correctionForm.correctionType,
        date: today,
        originalPunchId: correctionForm.originalPunchId || null,
        eventType: correctionForm.eventType,
        correctedTime: correctionForm.correctedTime,
        reason: correctionForm.reason,
        comment: correctionForm.comment || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'fr' ? 'Correction appliquée avec succès' : 'Correction applied successfully');
      setCorrectionDialogOpen(false);
      
      // Refresh presence data
      fetchPresence();
    } catch (error) {
      console.error('Failed to submit correction:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la correction' : 'Correction failed'));
    } finally {
      setSubmittingCorrection(false);
    }
  };

  // Comment functions
  const openCommentDialog = async () => {
    if (!selectedEmployeeProfile) return;
    
    setCommentDialogOpen(true);
    setCommentForm({
      commentType: 'general',
      content: '',
      isPrivate: false
    });
    setLoadingComments(true);
    
    try {
      const token = localStorage.getItem('punchtm-token');
      const response = await axios.get(
        `${API}/employee/${selectedEmployeeProfile.employee.id}/comments?tenantId=${effectiveTenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmployeeComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!selectedEmployeeProfile || !commentForm.content.trim()) {
      toast.error(language === 'fr' ? 'Veuillez entrer un commentaire' : 'Please enter a comment');
      return;
    }

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('punchtm-token');
      
      await axios.post(`${API}/employee/${selectedEmployeeProfile.employee.id}/comments`, {
        employeeId: selectedEmployeeProfile.employee.id,
        commentType: commentForm.commentType,
        content: commentForm.content,
        isPrivate: commentForm.isPrivate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(language === 'fr' ? 'Commentaire ajouté' : 'Comment added');
      
      // Refresh comments
      const response = await axios.get(
        `${API}/employee/${selectedEmployeeProfile.employee.id}/comments?tenantId=${effectiveTenantId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmployeeComments(response.data.comments || []);
      
      // Reset form
      setCommentForm({ ...commentForm, content: '' });
    } catch (error) {
      console.error('Failed to submit comment:', error);
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'ajout' : 'Failed to add comment'));
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!confirm(language === 'fr' ? 'Supprimer ce commentaire ?' : 'Delete this comment?')) return;
    
    try {
      const token = localStorage.getItem('punchtm-token');
      await axios.delete(
        `${API}/employee/${selectedEmployeeProfile.employee.id}/comments/${commentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'fr' ? 'Commentaire supprimé' : 'Comment deleted');
      setEmployeeComments(employeeComments.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Failed to delete');
    }
  };

  const renderAlertDetails = () => {
    if (!selectedAlertType) return null;
    
    const alertConfig = alertTypes.find(a => a.id === selectedAlertType);
    if (!alertConfig) return null;

    const allChecked = alertConfig.data.length > 0 && bulkSelected.length === alertConfig.data.length;
    const someChecked = bulkSelected.length > 0 && !allChecked;

    return (
      <div className="space-y-3">
        {alertConfig.data.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="mx-auto text-green-500 mb-3" size={40} />
            <p className="text-slate-600 font-medium">
              {language === 'fr' ? 'Toutes les alertes de ce type ont été traitées' : 'All alerts of this type have been treated'}
            </p>
          </div>
        ) : (
          <>
            {/* Bulk Action Bar */}
            <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-lg p-2.5 border dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allChecked}
                  ref={(el) => { if (el) el.dataset.indeterminate = someChecked; }}
                  onCheckedChange={() => toggleSelectAll(alertConfig.data)}
                  data-testid="bulk-select-all"
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {bulkSelected.length > 0
                    ? `${bulkSelected.length} / ${alertConfig.data.length} ${language === 'fr' ? 'sélectionné(s)' : 'selected'}`
                    : (language === 'fr' ? 'Tout sélectionner' : 'Select all')}
                </span>
              </div>
              {bulkSelected.length > 0 && (
                <Button
                  size="sm"
                  className="gap-1.5 h-7 text-xs"
                  onClick={openBulkDialog}
                  data-testid="bulk-treat-btn"
                >
                  <ListChecks size={14} />
                  {language === 'fr' ? 'Traiter la sélection' : 'Treat selection'} ({bulkSelected.length})
                </Button>
              )}
            </div>

            {/* Alert Items */}
            <div className="max-h-80 overflow-y-auto space-y-2">
              {alertConfig.data.map((item, index) => {
                const isChecked = bulkSelected.some(e => e.employeeId === item.employeeId);
                return (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border-2 transition-all hover:shadow-md ${isChecked ? 'border-primary/50 bg-primary/5 dark:bg-primary/10' : `${alertConfig.bgColor} ${alertConfig.borderColor}`}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleBulkSelect(item)}
                        data-testid={`bulk-check-${item.employeeCode}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm text-slate-900 dark:text-slate-100">{item.employeeName}</p>
                          <span className="text-[10px] font-mono bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">{item.employeeCode}</span>
                        </div>
                        <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                          {selectedAlertType === 'lateArrivals' && (
                            <span>
                              <span className={`font-bold ${alertConfig.color}`}>+{item.lateMinutes} min</span>
                              {' • '}
                              {language === 'fr' ? 'Attendu' : 'Expected'}: {item.expectedTime} → {item.actualTime}
                            </span>
                          )}
                          {selectedAlertType === 'absences' && (
                            <span>{language === 'fr' ? 'Quart' : 'Shift'}: {item.shiftName || '-'}</span>
                          )}
                          {selectedAlertType === 'longBreaks' && (
                            <span>
                              <span className={`font-bold ${alertConfig.color}`}>{item.breakDuration} min</span>
                              {' • '}
                              {language === 'fr' ? 'Autorisé' : 'Allowed'}: {item.standardDuration} min
                            </span>
                          )}
                          {selectedAlertType === 'incompletePunches' && (
                            <span>
                              {item.issue === 'missing_out' 
                                ? (language === 'fr' ? 'Sortie manquante' : 'Missing exit')
                                : (language === 'fr' ? 'Pause sans retour' : 'Break no return')
                              }
                              {' • '}
                              {language === 'fr' ? 'Dernier' : 'Last'}: {formatTime(item.lastPunchTime)}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button 
                        size="sm" variant="outline"
                        onClick={() => openTreatDialog(item, selectedAlertType)}
                        className="gap-1 h-7 text-xs shrink-0"
                        data-testid={`treat-alert-${item.employeeCode}`}
                      >
                        <Wrench size={12} />
                        {language === 'fr' ? 'Traiter' : 'Treat'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading || tenantLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  // Show error if tenant configuration is invalid
  if (tenantError) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="tenant-error">
        <div className="text-center p-8 bg-red-50 rounded-lg border border-red-200 max-w-lg">
          <XCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-red-800 mb-2">
            {language === 'fr' ? 'Erreur de configuration' : 'Configuration Error'}
          </h2>
          <p className="text-red-600">{tenantError}</p>
          <p className="mt-4 text-sm text-slate-500">
            {language === 'fr' 
              ? 'Veuillez contacter votre administrateur système pour corriger ce problème.'
              : 'Please contact your system administrator to fix this issue.'}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            data-testid="retry-btn"
          >
            {language === 'fr' ? 'Réessayer' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      {/* Header with global filters */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{t('dashboard')}</h1>
            <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              LIVE
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">
              {lastRefresh.toLocaleTimeString()}
            </span>
            <Button onClick={handleRefresh} variant="ghost" size="sm" disabled={refreshing} className="gap-1 h-8" data-testid="refresh-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={openHistoryDialog} variant="outline" size="sm" className="gap-1.5 h-8" data-testid="history-btn">
              <History size={14} />
              {language === 'fr' ? 'Historique' : 'History'}
            </Button>
          </div>
        </div>
        {/* Global filters bar */}
        <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900/60 rounded-lg border dark:border-slate-800" data-testid="global-filters">
          <Filter size={14} className="text-slate-400 ml-1" />
          <div className="relative flex-1 max-w-48">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="search-input"
            />
          </div>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="site-filter">
              <MapPin size={12} className="mr-1 text-slate-400" />
              <SelectValue placeholder={t('site')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'fr' ? 'Tous sites' : 'All sites'}</SelectItem>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedZone} onValueChange={setSelectedZone}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="zone-filter">
              <SelectValue placeholder={language === 'fr' ? 'Zone' : 'Zone'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'fr' ? 'Toutes zones' : 'All zones'}</SelectItem>
              {zones.map((zone) => (
                <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedShift} onValueChange={setSelectedShift}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="shift-filter">
              <Clock size={12} className="mr-1 text-slate-400" />
              <SelectValue placeholder={language === 'fr' ? 'Quart' : 'Shift'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'fr' ? 'Tous quarts' : 'All shifts'}</SelectItem>
              {shifts.map((shift) => (
                <SelectItem key={shift.id} value={shift.id}>{shift.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="status-filter">
              <SelectValue placeholder={t('status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'fr' ? 'Tous statuts' : 'All statuses'}</SelectItem>
              <SelectItem value="IN">{language === 'fr' ? 'Présent' : 'Present'}</SelectItem>
              <SelectItem value="BREAK">{language === 'fr' ? 'Pause' : 'Break'}</SelectItem>
              <SelectItem value="OUT">{language === 'fr' ? 'Sorti' : 'Out'}</SelectItem>
            </SelectContent>
          </Select>
          {isSupervisor && (
            <label className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded-md border border-blue-200 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                id="showAllEmployees"
                checked={showAllEmployees}
                onChange={(e) => {
                  setShowAllEmployees(e.target.checked);
                  if (e.target.checked) {
                    setSelectedShift('all');
                  } else if (supervisorShiftId) {
                    setSelectedShift(supervisorShiftId);
                  }
                }}
                className="rounded border-blue-300 text-blue-600 h-3.5 w-3.5"
              />
              {language === 'fr' ? 'Tous les employés' : 'All employees'}
            </label>
          )}
        </div>
      </div>

      {/* Hero KPI Row — Présents & Absents prominent */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Hero: Présents */}
        <Card className="lg:col-span-1 border-2 border-green-300 dark:border-green-800 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/40 dark:to-emerald-950/30 shadow-sm" data-testid="stat-presents">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide">{t('present')}</p>
                <p className="text-4xl font-black text-green-700 dark:text-green-300 mt-0.5 leading-none">{presence.stats.present}</p>
              </div>
              <div className="p-2.5 rounded-xl bg-green-200/60 dark:bg-green-800/40">
                <UserCheck size={22} className="text-green-700 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Hero: Absents */}
        <Card className="lg:col-span-1 border-2 border-red-300 dark:border-red-800 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/40 dark:to-rose-950/30 shadow-sm" data-testid="stat-absents">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide">{language === 'fr' ? 'Absents' : 'Absent'}</p>
                <p className="text-4xl font-black text-red-700 dark:text-red-300 mt-0.5 leading-none">{presence.stats.absent || 0}</p>
              </div>
              <div className="p-2.5 rounded-xl bg-red-200/60 dark:bg-red-800/40">
                <UserX size={22} className="text-red-700 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Secondary: Attendus */}
        <Card className="border border-slate-200 dark:border-slate-700" data-testid="stat-attendus">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
              <Users size={18} className="text-slate-600 dark:text-slate-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{language === 'fr' ? 'Attendus' : 'Expected'}</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-200">{presence.stats.expected || 0}</p>
            </div>
          </CardContent>
        </Card>
        {/* Secondary: Retards */}
        <Card className="border border-amber-200 dark:border-amber-800" data-testid="stat-retards">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40">
              <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-amber-600 dark:text-amber-400">{language === 'fr' ? 'Retards' : 'Late'}</p>
              <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{presence.stats.late || 0}</p>
            </div>
          </CardContent>
        </Card>
        {/* Secondary: En pause */}
        <Card className="border border-orange-200 dark:border-orange-800" data-testid="stat-pause">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/40">
              <Coffee size={18} className="text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-orange-600 dark:text-orange-400">{t('onBreak')}</p>
              <p className="text-xl font-bold text-orange-700 dark:text-orange-300">{presence.stats.onBreak}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* No schedule today — compact */}
      {!presence.stats.hasSchedulesToday && !presence.stats.isDefaultWorkDay && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800 text-sm" data-testid="no-schedule-banner">
          <CalendarX size={16} className="text-blue-500 dark:text-blue-400 shrink-0" />
          <span className="text-blue-800 dark:text-blue-300 font-medium">
            {language === 'fr' 
              ? `Aucune planification (${presence.stats.todayDayName ? (presence.stats.todayDayName.charAt(0).toUpperCase() + presence.stats.todayDayName.slice(1)) : ''}) — `
              : `No schedule (${presence.stats.todayDayName ? (presence.stats.todayDayName.charAt(0).toUpperCase() + presence.stats.todayDayName.slice(1)) : ''}) — `}
          </span>
          <span className="text-blue-600 dark:text-blue-400 text-xs">
            {language === 'fr'
              ? 'pas de retards/absences comptabilisés'
              : 'no late/absent counted'}
          </span>
        </div>
      )}

      {/* Priority Actions Today — only shown if there are untreated alerts */}
      {totalUntreatedAnomalies > 0 && (
        <Card className="border-2 border-red-200 dark:border-red-800 shadow-md bg-gradient-to-r from-red-50/50 to-amber-50/30 dark:from-red-950/30 dark:to-amber-950/20" data-testid="priority-actions">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-500" />
              <h3 className="font-heading font-bold text-slate-900 dark:text-white">
                {language === 'fr' ? 'Actions prioritaires aujourd\'hui' : 'Priority Actions Today'}
              </h3>
              <Badge className="bg-red-500 text-white animate-pulse">{totalUntreatedAnomalies}</Badge>
              {treatedAlerts.treatedCount > 0 && (
                <Badge className="bg-green-100 text-green-700 ml-auto">
                  <CheckCircle size={11} className="mr-1" />
                  {treatedAlerts.treatedCount} {language === 'fr' ? 'traitées' : 'treated'}
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {alertTypes.filter(a => a.count > 0).map((alert) => (
                <button
                  key={alert.id}
                  onClick={() => openAlertDialog(alert.id)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-150 hover:shadow-md hover:scale-[1.01] active:scale-[0.99] ${alert.bgColor} ${alert.borderColor}`}
                  data-testid={`priority-${alert.id}`}
                >
                  <div className={`p-2 rounded-lg ${alert.bgColor}`}>
                    <alert.icon size={20} className={alert.color} />
                  </div>
                  <div className="text-left">
                    <p className={`text-2xl font-black leading-none ${alert.color}`}>{alert.count}</p>
                    <p className="text-xs font-medium text-slate-600 mt-0.5">{alert.label}</p>
                  </div>
                  <ChevronRight size={16} className={`ml-auto ${alert.color}`} />
                </button>
              ))}
            </div>
            {/* Top 3 urgent alerts preview */}
            {alertTypes.flatMap(a => a.data.slice(0, 1).map(d => ({...d, alertType: a.id, alertLabel: a.label, alertColor: a.color, alertBg: a.bgColor}))).slice(0, 3).length > 0 && (
              <div className="mt-3 pt-3 border-t border-red-200/60 space-y-1.5">
                {alertTypes.flatMap(a => a.data.slice(0, 1).map(d => ({...d, alertType: a.id, alertLabel: a.label, alertColor: a.color, alertBg: a.bgColor}))).slice(0, 3).map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => openAlertDialog(item.alertType)}
                    className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-white/60 transition-colors text-left"
                    data-testid={`urgent-preview-${idx}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${item.alertColor === 'text-red-600' ? 'bg-red-500' : item.alertColor === 'text-amber-600' ? 'bg-amber-500' : item.alertColor === 'text-orange-600' ? 'bg-orange-500' : 'bg-purple-500'}`} />
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{item.employeeName}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{item.employeeCode}</span>
                    <Badge variant="outline" className={`text-xs ml-auto ${item.alertColor} border-current/20`}>{item.alertLabel}</Badge>
                    <ChevronRight size={14} className="text-slate-400 dark:text-slate-500" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* All clear message — compact */}
      {totalUntreatedAnomalies === 0 && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800" data-testid="alerts-clear">
          <CheckCircle size={18} className="text-green-500 dark:text-green-400" />
          <span className="text-green-700 dark:text-green-300 font-medium text-sm">
            {language === 'fr' ? 'Aucune alerte en attente — Tout est en ordre !' : 'No pending alerts — Everything is in order!'}
          </span>
          {treatedAlerts.treatedCount > 0 && (
            <Badge className="bg-green-100 text-green-700 ml-auto text-xs">
              {treatedAlerts.treatedCount} {language === 'fr' ? 'traitées' : 'treated'}
            </Badge>
          )}
        </div>
      )}

      {/* Overtime — compact inline */}
      <div className="flex items-center gap-3 p-3 bg-orange-50/60 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800" data-testid="overtime-widget">
        <Hourglass size={18} className="text-orange-500 dark:text-orange-400 shrink-0" />
        <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
          {language === 'fr' ? 'Heures supp.' : 'Overtime'}
        </span>
        {liveWorking.totalOvertime > 0 ? (
          <>
            <Badge className="bg-orange-500 text-white">{liveWorking.totalOvertime} {language === 'fr' ? 'en cours' : 'ongoing'}</Badge>
            <div className="flex -space-x-1 ml-1">
              {liveWorking.employees.filter(e => e.isOvertime).slice(0, 4).map((emp) => (
                <div key={emp.employeeId} className="w-7 h-7 rounded-full bg-orange-200 border-2 border-white flex items-center justify-center" title={`${emp.employeeName} ${emp.overtimeDisplay}`}>
                  <span className="text-[10px] font-bold text-orange-700">{emp.employeeName?.charAt(0)}</span>
                </div>
              ))}
              {liveWorking.employees.filter(e => e.isOvertime).length > 4 && (
                <div className="w-7 h-7 rounded-full bg-orange-300 border-2 border-white flex items-center justify-center">
                  <span className="text-[10px] font-bold text-orange-800">+{liveWorking.employees.filter(e => e.isOvertime).length - 4}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <span className="text-xs text-orange-600 dark:text-orange-400">{language === 'fr' ? 'Aucun actuellement' : 'None currently'}</span>
        )}
        <div className="flex gap-1.5 ml-auto">
          <Button variant="ghost" size="sm" onClick={handleGenerateDailyOvertime} className="h-7 px-2 text-orange-700 hover:bg-orange-100" data-testid="generate-overtime-btn">
            <RefreshCw size={12} className="mr-1" /> {language === 'fr' ? 'Générer' : 'Generate'}
          </Button>
          <Button variant="ghost" size="sm" onClick={openOvertimeDialog} className="h-7 px-2 text-orange-700 hover:bg-orange-100" data-testid="view-overtime-btn">
            <Eye size={12} className="mr-1" /> {language === 'fr' ? 'Voir' : 'View'}
          </Button>
        </div>
      </div>

      {/* Alert Details Dialog */}
      <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {selectedAlertType && (
                <>
                  {React.createElement(alertTypes.find(a => a.id === selectedAlertType)?.icon || AlertTriangle, {
                    size: 20,
                    className: alertTypes.find(a => a.id === selectedAlertType)?.color
                  })}
                  {alertTypes.find(a => a.id === selectedAlertType)?.label}
                  <Badge className={`ml-2 ${alertTypes.find(a => a.id === selectedAlertType)?.bgColor} ${alertTypes.find(a => a.id === selectedAlertType)?.color}`}>
                    {alertTypes.find(a => a.id === selectedAlertType)?.count} {language === 'fr' ? 'à traiter' : 'pending'}
                  </Badge>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {renderAlertDetails()}
        </DialogContent>
      </Dialog>

      {/* Treatment Dialog */}
      <Dialog open={treatDialogOpen} onOpenChange={setTreatDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wrench size={20} className="text-primary" />
              {language === 'fr' ? 'Traiter l\'alerte' : 'Treat Alert'}
            </DialogTitle>
            <DialogDescription>
              {selectedEmployee && (
                <span className="flex flex-col gap-1 mt-2">
                  <span className="font-semibold text-slate-900">{selectedEmployee.employeeName}</span>
                  <span className="text-sm">Code: {selectedEmployee.employeeCode}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-medium">
                {language === 'fr' ? 'Type de résolution' : 'Resolution type'} *
              </Label>
              <Select 
                value={treatmentForm.resolutionType} 
                onValueChange={(v) => setTreatmentForm({ ...treatmentForm, resolutionType: v })}
              >
                <SelectTrigger data-testid="resolution-select">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label[language] || type.label.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="font-medium">
                {language === 'fr' ? 'Commentaire (optionnel)' : 'Comment (optional)'}
              </Label>
              <Textarea
                value={treatmentForm.comment}
                onChange={(e) => setTreatmentForm({ ...treatmentForm, comment: e.target.value })}
                placeholder={language === 'fr' ? 'Ajouter un commentaire...' : 'Add a comment...'}
                rows={3}
                data-testid="comment-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTreatDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleTreatAlert} 
              disabled={treating || !treatmentForm.resolutionType}
              data-testid="confirm-treat-btn"
            >
              {treating ? (
                <RefreshCw className="animate-spin mr-2" size={16} />
              ) : (
                <CheckCircle className="mr-2" size={16} />
              )}
              {language === 'fr' ? 'Valider' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Treatment Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-md dark:bg-slate-900 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ListChecks size={18} className="text-primary" />
              {language === 'fr' ? 'Traitement groupé' : 'Bulk Treatment'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? `${bulkSelected.length} employé(s) sélectionné(s)`
                : `${bulkSelected.length} employee(s) selected`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            {/* Preview list */}
            <div className="max-h-28 overflow-y-auto space-y-1 bg-slate-50 dark:bg-slate-800 rounded-md p-2">
              {bulkSelected.map((emp, idx) => (
                <div key={idx} className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                  <CheckSquare size={10} className="text-primary shrink-0" />
                  {emp.employeeName} <span className="text-slate-400">({emp.employeeCode})</span>
                </div>
              ))}
            </div>
            
            {/* Resolution type */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{language === 'fr' ? 'Motif commun' : 'Resolution type'}</Label>
              <Select value={bulkForm.resolutionType} onValueChange={(v) => setBulkForm(prev => ({ ...prev, resolutionType: v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="bulk-resolution-select">
                  <SelectValue placeholder={language === 'fr' ? 'Choisir un motif...' : 'Select resolution...'} />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>{rt.label[language] || rt.label.fr}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Comment */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{language === 'fr' ? 'Commentaire (optionnel)' : 'Comment (optional)'}</Label>
              <Textarea
                value={bulkForm.comment}
                onChange={(e) => setBulkForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder={language === 'fr' ? 'Commentaire commun...' : 'Common comment...'}
                className="min-h-[60px] text-sm"
                data-testid="bulk-comment"
              />
            </div>

            {/* Warning */}
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md p-2.5">
              <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                {language === 'fr'
                  ? `Cette action sera appliquée à ${bulkSelected.length} employé(s). Toutes les actions sont journalisées.`
                  : `This action will be applied to ${bulkSelected.length} employee(s). All actions are logged.`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} className="h-9">
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={handleBulkTreat}
              disabled={!bulkForm.resolutionType || bulkProcessing}
              className="h-9 gap-1.5"
              data-testid="confirm-bulk-treat-btn"
            >
              {bulkProcessing && <RefreshCw size={14} className="animate-spin" />}
              {language === 'fr' ? `Traiter ${bulkSelected.length} alerte(s)` : `Treat ${bulkSelected.length} alert(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History size={20} className="text-primary" />
                {language === 'fr' ? 'Historique des traitements' : 'Treatment History'}
                {historyStats && (
                  <Badge className="bg-blue-100 text-blue-700 ml-2">
                    {historyStats.total} {language === 'fr' ? 'enregistrements' : 'records'}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <DownloadButton
                  url={buildAlertExportUrl('excel')}
                  className="gap-1"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet size={14} />
                  Excel
                </DownloadButton>
                <DownloadButton
                  url={buildAlertExportUrl('pdf')}
                  className="gap-1"
                  data-testid="export-pdf-btn"
                >
                  <Download size={14} />
                  PDF
                </DownloadButton>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg border">
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">
                <Filter size={12} className="inline mr-1" />
                {language === 'fr' ? 'Employé' : 'Employee'}
              </Label>
              <Select 
                value={historyFilters.employeeId} 
                onValueChange={(v) => handleHistoryFilterChange('employeeId', v)}
              >
                <SelectTrigger className="h-9" data-testid="history-employee-filter">
                  <SelectValue placeholder={language === 'fr' ? 'Tous' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les employés' : 'All employees'}</SelectItem>
                  {historyEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">
                {language === 'fr' ? 'Type d\'événement' : 'Event type'}
              </Label>
              <Select 
                value={historyFilters.alertType} 
                onValueChange={(v) => handleHistoryFilterChange('alertType', v)}
              >
                <SelectTrigger className="h-9" data-testid="history-type-filter">
                  <SelectValue placeholder={language === 'fr' ? 'Tous' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les types' : 'All types'}</SelectItem>
                  <SelectItem value="absences">{language === 'fr' ? 'Absences' : 'Absences'}</SelectItem>
                  <SelectItem value="lateArrivals">{language === 'fr' ? 'Retards' : 'Late arrivals'}</SelectItem>
                  <SelectItem value="longBreaks">{language === 'fr' ? 'Pauses longues' : 'Long breaks'}</SelectItem>
                  <SelectItem value="incompletePunches">{language === 'fr' ? 'Punch incomplets' : 'Incomplete punches'}</SelectItem>
                  <SelectItem value="punch_correction">{language === 'fr' ? 'Punch corrigé' : 'Punch corrected'}</SelectItem>
                  <SelectItem value="overtime">{language === 'fr' ? 'Overtime' : 'Overtime'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">
                {language === 'fr' ? 'Date début' : 'Start date'}
              </Label>
              <Input
                type="date"
                value={historyFilters.startDate}
                onChange={(e) => handleHistoryFilterChange('startDate', e.target.value)}
                className="h-9"
                data-testid="history-start-date"
              />
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600">
                {language === 'fr' ? 'Date fin' : 'End date'}
              </Label>
              <Input
                type="date"
                value={historyFilters.endDate}
                onChange={(e) => handleHistoryFilterChange('endDate', e.target.value)}
                className="h-9"
                data-testid="history-end-date"
              />
            </div>
          </div>
          
          <div className="overflow-y-auto max-h-80">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="animate-spin text-primary" size={32} />
              </div>
            ) : treatmentHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText size={40} className="mx-auto mb-3 text-slate-300" />
                <p>{language === 'fr' ? 'Aucun traitement enregistré' : 'No treatments recorded'}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-24">{language === 'fr' ? 'Date' : 'Date'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Employé' : 'Employee'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Type' : 'Type'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Détail' : 'Detail'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Résolution' : 'Resolution'}</TableHead>
                    <TableHead>{language === 'fr' ? 'Traité par' : 'Treated by'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {treatmentHistory.map((item) => (
                    <TableRow key={item.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm font-mono">{item.date}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{item.employeeName}</p>
                          <p className="text-xs text-slate-500">{item.employeeCode}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${
                          item.alertType === 'absences' ? 'border-red-300 text-red-700' :
                          item.alertType === 'lateArrivals' ? 'border-amber-300 text-amber-700' :
                          item.alertType === 'longBreaks' ? 'border-orange-300 text-orange-700' :
                          'border-slate-300 text-slate-700'
                        }`}>
                          {item.alertType === 'absences' ? (language === 'fr' ? 'Absence' : 'Absence') :
                           item.alertType === 'lateArrivals' ? (language === 'fr' ? 'Retard' : 'Late') :
                           item.alertType === 'longBreaks' ? (language === 'fr' ? 'Pause longue' : 'Long break') :
                           item.alertType === 'incompletePunches' ? (language === 'fr' ? 'Punch incomplet' : 'Incomplete') :
                           item.alertType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-32 truncate" title={item.comment || '-'}>
                        {item.comment || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${
                          item.resolutionType === 'sickness' ? 'bg-blue-100 text-blue-700' :
                          item.resolutionType === 'approved_leave' ? 'bg-green-100 text-green-700' :
                          item.resolutionType === 'justified_late' ? 'bg-amber-100 text-amber-700' :
                          item.resolutionType === 'ignored' ? 'bg-slate-100 text-slate-600' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {item.resolutionLabel || item.resolutionType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{item.treatedByName}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee Quick Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User size={20} className="text-primary" />
              {language === 'fr' ? 'Fiche employé' : 'Employee Profile'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingProfile ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-primary" size={32} />
            </div>
          ) : selectedEmployeeProfile ? (
            <div className="space-y-4">
              {/* Employee Info */}
              <div className="p-4 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="text-primary" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">
                      {selectedEmployeeProfile.employee.firstName} {selectedEmployeeProfile.employee.lastName}
                    </h3>
                    <p className="text-sm text-slate-500 font-mono">
                      {selectedEmployeeProfile.employee.employeeCode}
                    </p>
                  </div>
                  {selectedEmployeeProfile.shift && (
                    <Badge className="bg-blue-100 text-blue-700">
                      <Clock size={12} className="mr-1" />
                      {selectedEmployeeProfile.shift.name}
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Today's Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                  <p className="text-xs text-green-600 mb-1">{language === 'fr' ? 'Entrée' : 'Entry'}</p>
                  <p className="font-bold text-green-700">
                    {selectedEmployeeProfile.today.firstIn 
                      ? new Date(selectedEmployeeProfile.today.firstIn).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                  <p className="text-xs text-blue-600 mb-1">{language === 'fr' ? 'Travaillé' : 'Worked'}</p>
                  <p className="font-bold text-blue-700">
                    {selectedEmployeeProfile.today.workedMinutes > 0 
                      ? `${Math.floor(selectedEmployeeProfile.today.workedMinutes / 60)}h ${Math.round(selectedEmployeeProfile.today.workedMinutes % 60)}m`
                      : '-'}
                  </p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-center">
                  <p className="text-xs text-orange-600 mb-1">{language === 'fr' ? 'Pause' : 'Break'}</p>
                  <p className="font-bold text-orange-700">
                    {selectedEmployeeProfile.today.breakMinutes > 0 
                      ? `${selectedEmployeeProfile.today.breakMinutes}m`
                      : '-'}
                  </p>
                </div>
              </div>
              
              {/* History Stats */}
              {selectedEmployeeProfile.history.stats.total > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-amber-700 mb-2">
                    {language === 'fr' ? 'Alertes ce mois' : 'Alerts this month'}: {selectedEmployeeProfile.history.stats.thisMonth}
                    <span className="text-amber-500 ml-2">
                      ({language === 'fr' ? 'Total' : 'Total'}: {selectedEmployeeProfile.history.stats.total})
                    </span>
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {Object.entries(selectedEmployeeProfile.history.stats.byType).map(([type, count]) => (
                      <Badge key={type} variant="outline" className="text-xs">
                        {type}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={() => {
                    setProfileDialogOpen(false);
                    setHistoryFilters({ 
                      ...historyFilters, 
                      employeeId: selectedEmployeeProfile.employee.id 
                    });
                    openHistoryDialog();
                  }}
                  data-testid="view-employee-history"
                >
                  <History size={16} />
                  {language === 'fr' ? 'Voir historique' : 'View history'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={openCorrectionDialog}
                  data-testid="correct-punch"
                >
                  <Edit size={16} />
                  {language === 'fr' ? 'Corriger punch' : 'Correct punch'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 gap-2"
                  onClick={openCommentDialog}
                  data-testid="add-comment"
                >
                  <MessageSquare size={16} />
                  {language === 'fr' ? 'Commentaire' : 'Comment'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p>{language === 'fr' ? 'Employé non trouvé' : 'Employee not found'}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Punch Correction Dialog */}
      <Dialog open={correctionDialogOpen} onOpenChange={setCorrectionDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit size={20} className="text-primary" />
              {language === 'fr' ? 'Corriger un pointage' : 'Correct a punch'}
            </DialogTitle>
            <DialogDescription>
              {selectedEmployeeProfile && (
                <span className="font-medium">
                  {selectedEmployeeProfile.employee.firstName} {selectedEmployeeProfile.employee.lastName} ({selectedEmployeeProfile.employee.employeeCode})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Today's punches */}
            {loadingPunches ? (
              <div className="flex items-center justify-center py-4">
                <RefreshCw className="animate-spin text-primary" size={24} />
              </div>
            ) : employeePunches.length > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-xs font-medium text-slate-500 mb-2">
                  {language === 'fr' ? 'Pointages du jour' : "Today's punches"}
                </p>
                <div className="space-y-1">
                  {employeePunches.map((punch, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="font-mono">{punch.eventType}</span>
                      <span className="text-slate-600">
                        {new Date(punch.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Type de correction' : 'Correction type'} *</Label>
              <Select 
                value={correctionForm.correctionType} 
                onValueChange={(v) => setCorrectionForm({ ...correctionForm, correctionType: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add_missing">{language === 'fr' ? 'Ajouter pointage manquant' : 'Add missing punch'}</SelectItem>
                  <SelectItem value="modify_time">{language === 'fr' ? 'Modifier l\'heure' : 'Modify time'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type de punch' : 'Punch type'} *</Label>
                <Select 
                  value={correctionForm.eventType} 
                  onValueChange={(v) => setCorrectionForm({ ...correctionForm, eventType: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IN">{language === 'fr' ? 'Entrée' : 'Clock In'}</SelectItem>
                    <SelectItem value="OUT">{language === 'fr' ? 'Sortie' : 'Clock Out'}</SelectItem>
                    <SelectItem value="BREAK_START">{language === 'fr' ? 'Début pause' : 'Break Start'}</SelectItem>
                    <SelectItem value="BREAK_END">{language === 'fr' ? 'Fin pause' : 'Break End'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Heure corrigée' : 'Corrected time'} *</Label>
                <Input
                  type="time"
                  value={correctionForm.correctedTime}
                  onChange={(e) => setCorrectionForm({ ...correctionForm, correctedTime: e.target.value })}
                  data-testid="correction-time"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Raison' : 'Reason'} *</Label>
              <Select 
                value={correctionForm.reason} 
                onValueChange={(v) => setCorrectionForm({ ...correctionForm, reason: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="forgot_punch">{language === 'fr' ? 'Oubli de pointage' : 'Forgot to punch'}</SelectItem>
                  <SelectItem value="system_error">{language === 'fr' ? 'Erreur système' : 'System error'}</SelectItem>
                  <SelectItem value="wrong_time">{language === 'fr' ? 'Heure incorrecte' : 'Wrong time'}</SelectItem>
                  <SelectItem value="device_malfunction">{language === 'fr' ? 'Dysfonctionnement appareil' : 'Device malfunction'}</SelectItem>
                  <SelectItem value="other">{language === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Commentaire (optionnel)' : 'Comment (optional)'}</Label>
              <Textarea
                value={correctionForm.comment}
                onChange={(e) => setCorrectionForm({ ...correctionForm, comment: e.target.value })}
                placeholder={language === 'fr' ? 'Détails de la correction...' : 'Correction details...'}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSubmitCorrection} 
              disabled={submittingCorrection || !correctionForm.correctedTime}
              data-testid="submit-correction"
            >
              {submittingCorrection ? (
                <RefreshCw className="animate-spin mr-2" size={16} />
              ) : (
                <CheckCircle className="mr-2" size={16} />
              )}
              {language === 'fr' ? 'Appliquer' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquare size={20} className="text-primary" />
              {language === 'fr' ? 'Commentaires employé' : 'Employee Comments'}
            </DialogTitle>
            <DialogDescription>
              {selectedEmployeeProfile && (
                <span className="font-medium">
                  {selectedEmployeeProfile.employee.firstName} {selectedEmployeeProfile.employee.lastName}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add comment form */}
            <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{language === 'fr' ? 'Type' : 'Type'}</Label>
                  <Select 
                    value={commentForm.commentType} 
                    onValueChange={(v) => setCommentForm({ ...commentForm, commentType: v })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">{language === 'fr' ? 'Général' : 'General'}</SelectItem>
                      <SelectItem value="performance">{language === 'fr' ? 'Performance' : 'Performance'}</SelectItem>
                      <SelectItem value="incident">{language === 'fr' ? 'Incident' : 'Incident'}</SelectItem>
                      <SelectItem value="medical">{language === 'fr' ? 'Médical' : 'Medical'}</SelectItem>
                      <SelectItem value="administrative">{language === 'fr' ? 'Administratif' : 'Administrative'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={commentForm.isPrivate}
                      onChange={(e) => setCommentForm({ ...commentForm, isPrivate: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    {language === 'fr' ? 'Privé (admin seulement)' : 'Private (admin only)'}
                  </label>
                </div>
              </div>
              <Textarea
                value={commentForm.content}
                onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
                placeholder={language === 'fr' ? 'Écrire un commentaire...' : 'Write a comment...'}
                rows={2}
                data-testid="comment-content"
              />
              <Button 
                size="sm"
                onClick={handleSubmitComment}
                disabled={submittingComment || !commentForm.content.trim()}
                className="w-full gap-2"
                data-testid="submit-comment"
              >
                {submittingComment ? (
                  <RefreshCw className="animate-spin" size={14} />
                ) : (
                  <Plus size={14} />
                )}
                {language === 'fr' ? 'Ajouter' : 'Add'}
              </Button>
            </div>
            
            {/* Comments list */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {loadingComments ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="animate-spin text-primary" size={24} />
                </div>
              ) : employeeComments.length === 0 ? (
                <div className="text-center py-4 text-slate-500 text-sm">
                  {language === 'fr' ? 'Aucun commentaire' : 'No comments'}
                </div>
              ) : (
                employeeComments.map((comment) => (
                  <div key={comment.id} className={`p-3 rounded-lg border ${comment.isPrivate ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">
                            {comment.commentType}
                          </Badge>
                          {comment.isPrivate && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              {language === 'fr' ? 'Privé' : 'Private'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{comment.content}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {comment.createdByName} • {new Date(comment.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <XCircle size={14} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* LIVE Working — compact strip */}
      <Card className="border border-green-200 dark:border-green-800 shadow-sm" data-testid="live-working-section">
        <CardContent className="p-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="relative">
              <Timer size={18} className="text-green-600 dark:text-green-400" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            </div>
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{language === 'fr' ? 'Au travail' : 'At Work'}</span>
            <Badge className="bg-green-100 text-green-700 text-xs">{liveWorking.totalWorking}</Badge>
            {liveWorking.totalOvertime > 0 && (
              <Badge className="bg-red-100 text-red-700 text-xs animate-pulse">{liveWorking.totalOvertime} OT</Badge>
            )}
          </div>
          {liveWorking.employees?.length > 0 ? (
            <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {liveWorking.employees?.slice(0, 16).map((emp) => (
                <button
                  key={emp.employeeId}
                  onClick={() => openEmployeeProfile({ employeeId: emp.employeeId, employeeName: emp.employeeName, employeeCode: emp.employeeCode })}
                  className={`p-2 rounded-lg border text-center transition-all hover:shadow-sm hover:scale-[1.02] cursor-pointer ${
                    emp.isOvertime ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800' : 'bg-white dark:bg-slate-800 border-green-100 dark:border-green-800 hover:border-green-300 dark:hover:border-green-600'
                  }`}
                  data-testid={`live-worker-${emp.employeeCode}`}
                >
                  <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{emp.employeeName}</p>
                  <p className={`text-lg font-bold leading-tight ${emp.isOvertime ? 'text-red-600' : 'text-green-600'}`}>
                    {emp.workedDisplay}
                  </p>
                  {emp.isOvertime && (
                    <p className="text-[10px] text-red-500 font-medium">+{emp.overtimeDisplay}</p>
                  )}
                </button>
              ))}
              {liveWorking.employees?.length > 16 && (
                <div className="p-2 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
                  <span className="text-xs text-slate-500">+{liveWorking.employees.length - 16}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400 text-center py-2">{language === 'fr' ? 'Aucun employé au travail' : 'No employees at work'}</p>
          )}
        </CardContent>
      </Card>

      {/* Schedule Status — compact bar */}
      <div className="grid grid-cols-3 gap-2" data-testid="schedule-status-section">
        <div className="flex items-center gap-2 p-2.5 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
          <CalendarCheck size={16} className="text-green-600 dark:text-green-400" />
          <div>
            <p className="text-lg font-bold text-green-700 dark:text-green-300 leading-none">{scheduleStatus.stats?.scheduledPresent || 0}</p>
            <p className="text-[10px] text-green-600 dark:text-green-400">{language === 'fr' ? 'Planifiés présents' : 'Sched. present'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-2.5 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
          <CalendarX size={16} className="text-red-600 dark:text-red-400" />
          <div>
            <p className="text-lg font-bold text-red-700 dark:text-red-300 leading-none">{scheduleStatus.stats?.scheduledAbsent || 0}</p>
            <p className="text-[10px] text-red-600 dark:text-red-400">{language === 'fr' ? 'Planifiés absents' : 'Sched. absent'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-2.5 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
          <UserCheck size={16} className="text-amber-600 dark:text-amber-400" />
          <div>
            <p className="text-lg font-bold text-amber-700 dark:text-amber-300 leading-none">{scheduleStatus.stats?.unscheduledPresent || 0}</p>
            <p className="text-[10px] text-amber-600 dark:text-amber-400">{language === 'fr' ? 'Non planif. présents' : 'Unsched. present'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Presence Table — improved */}
        <div className="lg:col-span-2">
          <Card className="border shadow-sm">
            <CardHeader className="pb-2 pt-3 px-4 border-b bg-slate-50/50 dark:bg-slate-800/50">
              <CardTitle className="font-heading flex items-center gap-2 text-base text-slate-900 dark:text-white">
                <Users size={16} />
                {t('realTimePresence')}
                <Badge variant="outline" className="text-xs ml-2">{presence.employees.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100/80 dark:bg-slate-800/80">
                      <TableHead className="font-bold text-xs py-2">{t('employee')}</TableHead>
                      <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Zone' : 'Zone'}</TableHead>
                      <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Quart' : 'Shift'}</TableHead>
                      <TableHead className="font-bold text-xs py-2">{t('status')}</TableHead>
                      <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Entrée' : 'Entry'}</TableHead>
                      <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Durée' : 'Duration'}</TableHead>
                      <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Pause' : 'Break'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {presence.employees.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                          {t('noData')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      presence.employees.map((emp) => {
                        const rowBg = emp.currentStatus === 'IN' ? 'bg-green-50/40 hover:bg-green-50 dark:bg-green-950/20 dark:hover:bg-green-950/40' 
                          : emp.currentStatus === 'BREAK' ? 'bg-orange-50/40 hover:bg-orange-50 dark:bg-orange-950/20 dark:hover:bg-orange-950/40' 
                          : 'bg-slate-50/40 hover:bg-slate-50 dark:bg-slate-800/20 dark:hover:bg-slate-800/40';
                        return (
                          <TableRow 
                            key={emp.employeeId} 
                            className={`${rowBg} cursor-pointer transition-colors border-l-3`}
                            data-testid={`employee-row-${emp.employeeCode}`}
                            onClick={() => openEmployeeProfile(emp)}
                          >
                            <TableCell className="py-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full shrink-0 ${
                                  emp.currentStatus === 'IN' ? 'bg-green-500' : emp.currentStatus === 'BREAK' ? 'bg-orange-400' : 'bg-slate-300'
                                }`} />
                                <div>
                                  <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{emp.employeeName}</p>
                                  <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{emp.employeeCode}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="py-2 text-xs text-slate-600 dark:text-slate-400">
                              {emp.zoneName ? (
                                <span className="flex items-center gap-1">
                                  <MapPin size={11} className="text-slate-400 dark:text-slate-500" />
                                  {emp.zoneName}
                                </span>
                              ) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                            </TableCell>
                            <TableCell className="py-2 text-xs text-slate-600 dark:text-slate-400">{emp.shiftName || <span className="text-slate-300 dark:text-slate-600">-</span>}</TableCell>
                            <TableCell className="py-2">{getStatusBadge(emp.currentStatus)}</TableCell>
                            <TableCell className="py-2 font-mono text-xs text-green-600 font-medium">{formatTime(emp.firstInTime)}</TableCell>
                            <TableCell className="py-2">
                              {emp.presenceMinutes > 0 ? (
                                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{formatDuration(emp.presenceMinutes)}</span>
                              ) : <span className="text-slate-300 dark:text-slate-600 text-xs">-</span>}
                            </TableCell>
                            <TableCell className={`py-2 text-xs font-medium ${emp.breakMinutes > 30 ? 'text-orange-600 dark:text-orange-400' : 'text-slate-500 dark:text-slate-400'}`}>
                              {emp.breakMinutes > 0 ? formatDuration(emp.breakMinutes) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Punches — clickable, improved */}
        <div>
          <Card className="border shadow-sm">
            <CardHeader className="pb-2 pt-3 px-4 border-b bg-slate-50/50 dark:bg-slate-800/50">
              <CardTitle className="font-heading flex items-center gap-2 text-base text-slate-900 dark:text-white">
                <Clock size={16} />
                {language === 'fr' ? 'Derniers pointages' : 'Recent Punches'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {recentPunches.length === 0 ? (
                  <div className="p-4 text-center text-slate-400 text-xs">
                    {t('noData')}
                  </div>
                ) : (
                  recentPunches.map((punch, index) => (
                    <button
                      key={index}
                      onClick={() => openEmployeeProfile({ employeeId: punch.employeeId, employeeName: punch.employeeName, employeeCode: punch.employeeCode })}
                      className="w-full p-2.5 hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors flex items-center justify-between gap-2 text-left"
                      data-testid={`recent-punch-${index}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          punch.eventType === 'IN' ? 'bg-green-100 dark:bg-green-900/40' : 
                          punch.eventType === 'OUT' ? 'bg-red-100 dark:bg-red-900/40' : 
                          punch.eventType === 'BREAK_START' ? 'bg-orange-100 dark:bg-orange-900/40' : 'bg-blue-100 dark:bg-blue-900/40'
                        }`}>
                          {getPunchIcon(punch.eventType)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-xs text-slate-900 dark:text-slate-100 truncate">{punch.employeeName}</p>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500">{getPunchTypeLabel(punch.eventType)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="font-mono text-xs text-slate-600 dark:text-slate-400 font-medium">{formatTime(punch.timestamp)}</span>
                        <ChevronRight size={12} className="text-slate-300 dark:text-slate-600" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Overtime Validation Dialog */}
      <Dialog open={overtimeDialogOpen} onOpenChange={setOvertimeDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Hourglass size={20} className="text-orange-500" />
              {language === 'fr' ? 'Validation des heures supplémentaires' : 'Overtime Validation'}
              {pendingOvertime.length > 0 && (
                <Badge className="bg-orange-500 text-white ml-2">
                  {pendingOvertime.length} {language === 'fr' ? 'en attente' : 'pending'}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Approuvez ou refusez les demandes d\'heures supplémentaires' 
                : 'Approve or reject overtime requests'}
            </DialogDescription>
          </DialogHeader>
          
          {loadingOvertime ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-orange-500" size={32} />
            </div>
          ) : pendingOvertime.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
              <p className="text-slate-600">
                {language === 'fr' 
                  ? 'Aucune demande d\'heures supplémentaires en attente'
                  : 'No pending overtime requests'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingOvertime.map((record) => (
                <div 
                  key={record.id} 
                  className={`p-4 rounded-lg border-2 ${
                    selectedOvertimeRecord?.id === record.id 
                      ? 'border-orange-400 bg-orange-50' 
                      : 'border-slate-200 hover:border-orange-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <User size={20} className="text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium">{record.employeeName}</p>
                        <p className="text-sm text-slate-500">
                          {record.employeeCode} • {record.date}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-orange-600">
                        +{formatMinutesToDisplay(record.overtimeMinutes)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatMinutesToDisplay(record.workedMinutes)} / {formatMinutesToDisplay(record.standardMinutes)}
                      </p>
                    </div>
                  </div>
                  
                  {selectedOvertimeRecord?.id === record.id ? (
                    <div className="space-y-3 pt-3 border-t">
                      <div className="flex gap-3">
                        <Button
                          variant={overtimeApprovalForm.action === 'approve' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setOvertimeApprovalForm({...overtimeApprovalForm, action: 'approve'})}
                          className={overtimeApprovalForm.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                        >
                          <Check size={14} className="mr-1" />
                          {language === 'fr' ? 'Approuver' : 'Approve'}
                        </Button>
                        <Button
                          variant={overtimeApprovalForm.action === 'reject' ? 'destructive' : 'outline'}
                          size="sm"
                          onClick={() => setOvertimeApprovalForm({...overtimeApprovalForm, action: 'reject'})}
                        >
                          <X size={14} className="mr-1" />
                          {language === 'fr' ? 'Refuser' : 'Reject'}
                        </Button>
                      </div>
                      
                      {overtimeApprovalForm.action === 'approve' && (
                        <div className="flex items-center gap-2">
                          <Label className="text-sm whitespace-nowrap">
                            {language === 'fr' ? 'Minutes approuvées :' : 'Approved minutes:'}
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={overtimeApprovalForm.adjustedMinutes}
                            onChange={(e) => setOvertimeApprovalForm({
                              ...overtimeApprovalForm, 
                              adjustedMinutes: e.target.value
                            })}
                            className="w-24"
                            data-testid="overtime-minutes-input"
                          />
                          <span className="text-sm text-slate-500">
                            ({formatMinutesToDisplay(parseInt(overtimeApprovalForm.adjustedMinutes) || 0)})
                          </span>
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-sm">
                          {language === 'fr' ? 'Commentaire (optionnel)' : 'Comment (optional)'}
                        </Label>
                        <Input
                          value={overtimeApprovalForm.comment}
                          onChange={(e) => setOvertimeApprovalForm({
                            ...overtimeApprovalForm, 
                            comment: e.target.value
                          })}
                          placeholder={language === 'fr' ? 'Raison...' : 'Reason...'}
                          data-testid="overtime-comment-input"
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSubmitOvertimeReview}
                          disabled={submittingOvertimeReview}
                          className={overtimeApprovalForm.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                          data-testid="submit-overtime-review-btn"
                        >
                          {submittingOvertimeReview ? (
                            <RefreshCw className="animate-spin mr-2" size={14} />
                          ) : null}
                          {language === 'fr' ? 'Confirmer' : 'Confirm'}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => setSelectedOvertimeRecord(null)}
                        >
                          {language === 'fr' ? 'Annuler' : 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => selectOvertimeForReview(record)}
                      className="w-full mt-2"
                      data-testid={`review-overtime-${record.id}`}
                    >
                      {language === 'fr' ? 'Valider' : 'Review'}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
